/**
 * Composant Footer réutilisable
 * 
 * @example
 * // Utilisation basique
 * import { Footer } from '@/components/Footer';
 * 
 * function MaPage() {
 *   return (
 *     <div>
 *       // ... contenu de votre page
 *       <Footer />
 *     </div>
 *   );
 * }
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { getApiUrl } from '../config/api';
import { useTurnstile } from '../hooks/useTurnstile';
import { TURNSTILE_ACTION_NEWSLETTER } from '../config/turnstile';

/** Codes renvoyés par /api/newsletter/join -> clé i18n du message d'erreur. */
const NEWSLETTER_ERROR_KEYS: Record<string, string> = {
  invalid: 'landing.footer.newsletter_invalid',
  turnstile: 'landing.footer.newsletter_bot_check',
  rate_limited: 'landing.footer.newsletter_rate_limited',
  paused: 'landing.footer.newsletter_rate_limited',
};

interface FooterProps {
  /** Couleur de fond personnalisée (optionnel, défaut: '#FFFFFF') */
  backgroundColor?: string;
  /** Afficher la barre de séparation en haut (optionnel, défaut: true) */
  showSeparator?: boolean;
  /** Variante sombre, utilisee uniquement sur certaines pages comme la landing */
  isDark?: boolean;
}

export const Footer: React.FC<FooterProps> = ({ 
  backgroundColor = '#FFFFFF',
  showSeparator = true,
  isDark = false
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  // Honeypot : champ invisible, un humain ne le remplit jamais
  const [website, setWebsite] = useState('');
  const turnstile = useTurnstile(TURNSTILE_ACTION_NEWSLETTER, isDark ? 'dark' : 'light');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setMessage({ type: 'error', text: t('landing.footer.newsletter_invalid') });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      let turnstileToken = '';
      try {
        turnstileToken = await turnstile.getToken();
      } catch {
        throw new Error(t('landing.footer.newsletter_bot_check'));
      }

      const response = await fetch(getApiUrl('/api/newsletter/join'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, turnstileToken, website }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error(t('landing.footer.newsletter_already'));
        }
        const key = typeof data.code === 'string' ? NEWSLETTER_ERROR_KEYS[data.code] : undefined;
        throw new Error(key ? t(key) : t('landing.footer.newsletter_error'));
      }

      setMessage({ type: 'success', text: t('landing.footer.newsletter_success') });
      setEmail('');
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : t('landing.footer.newsletter_generic_error')
      });
    } finally {
      turnstile.reset();
      setIsLoading(false);
    }
  };

  const footerBackground = backgroundColor;
  const footerBorder = isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e5e7eb';
  const headingColor = isDark ? '#FFFFFF' : '#000000';
  const bodyColor = isDark ? 'rgba(255,255,255,0.45)' : '#4b5563';
  const hoverColor = isDark ? 'hover:text-white' : 'hover:text-gray-900';
  const newsletterInputBg = isDark ? '#151516' : '#FFFFFF';
  const newsletterInputText = isDark ? '#FFFFFF' : '#000000';
  const newsletterInputPlaceholder = isDark ? 'rgba(255,255,255,0.28)' : '#9ca3af';
  const newsletterInputBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #D1D5DB';
  const newsletterButtonBg = isDark ? '#FFFFFF' : '#000000';
  const newsletterButtonHover = isDark ? 'hover:bg-gray-100' : 'hover:bg-gray-900';
  const newsletterArrowColor = isDark ? '#0B0B0C' : '#FFFFFF';

  return (
    <footer 
      className="py-8 sm:py-10 px-8 sm:px-12 md:px-16 lg:px-32" 
      style={{ backgroundColor: footerBackground, borderTop: footerBorder }}
    >
      <div className="max-w-[1920px] mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 max-w-7xl mx-auto">
          {/* Section Newsletter - Premier sur mobile */}
          <div className="text-left order-1 lg:order-4">
            <h3 className="mb-4" style={{ color: headingColor, fontWeight: 400 }}>{t('landing.footer.newsletter_title')} <strong style={{ fontWeight: 700 }}>{t('landing.footer.newsletter_title_strong')}</strong></h3>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <style>{`
                .newsletter-input {
                  background: ${isDark ? '#151516' : '#FFFFFF'} !important;
                  color: ${isDark ? '#FFFFFF' : '#000000'} !important;
                  border-color: ${isDark ? 'rgba(255,255,255,0.08)' : '#D1D5DB'} !important;
                  box-shadow: ${isDark ? 'inset 0 0 0 1000px #151516' : 'inset 0 0 0 1000px #FFFFFF'} !important;
                  -webkit-box-shadow: ${isDark ? 'inset 0 0 0 1000px #151516' : 'inset 0 0 0 1000px #FFFFFF'} !important;
                }

                .newsletter-input::placeholder {
                  color: ${newsletterInputPlaceholder} !important;
                }

                .newsletter-input:-webkit-autofill,
                .newsletter-input:-webkit-autofill:hover,
                .newsletter-input:-webkit-autofill:focus,
                .newsletter-input:-webkit-autofill:active {
                  -webkit-box-shadow: 0 0 0 30px ${isDark ? '#151516' : 'white'} inset !important;
                  -webkit-text-fill-color: ${isDark ? 'white' : 'black'} !important;
                  background-color: ${isDark ? '#151516' : 'white'} !important;
                  transition: background-color 5000s ease-in-out 0s;
                }
              `}</style>
              <div
                className="relative"
                style={{
                  background: isDark ? '#151516' : '#FFFFFF',
                  border: newsletterInputBorder,
                  borderRadius: '9999px'
                }}
              >
                <input
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={turnstile.preload}
                  placeholder={t('landing.footer.newsletter_placeholder')}
                  disabled={isLoading}
                  className="newsletter-input w-full px-4 py-3 pr-14 !rounded-full !border focus:!outline-none focus:!shadow-none disabled:!opacity-50"
                  style={{ 
                    background: 'transparent',
                    backgroundColor: 'transparent',
                    color: newsletterInputText,
                    WebkitTextFillColor: newsletterInputText,
                    caretColor: newsletterInputText,
                    colorScheme: isDark ? 'dark' : 'light',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    borderRadius: '9999px',
                    border: 'none',
                    boxShadow: 'none',
                    WebkitBoxShadow: 'none',
                    opacity: 1
                  }}
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  aria-label={t('landing.footer.newsletter_submit')}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed ${newsletterButtonHover}`}
                  style={{ background: newsletterButtonBg }}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: newsletterArrowColor, borderTopColor: 'transparent' }} />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke={newsletterArrowColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>
              {/* Honeypot anti-bot : hors écran, non focusable, ignoré par l'autofill */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              />
              {/* Turnstile (managed) : vide sauf si Cloudflare demande une interaction */}
              <div ref={turnstile.containerRef} />
              {message && (
                <p className={`text-sm flex items-center gap-2 ${message.type === 'success' ? '' : 'text-red-600'}`} style={message.type === 'success' ? { color: '#10b981' } : {}}>
                  {message.type === 'success' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM16.0303 8.96967C16.3232 9.26256 16.3232 9.73744 16.0303 10.0303L11.0303 15.0303C10.7374 15.3232 10.2626 15.3232 9.96967 15.0303L7.96967 13.0303C7.67678 12.7374 7.67678 12.2626 7.96967 11.9697C8.26256 11.6768 8.73744 11.6768 9.03033 11.9697L10.5 13.4393L12.7348 11.2045L14.9697 8.96967C15.2626 8.67678 15.7374 8.67678 16.0303 8.96967Z" fill="#10b981"/>
                    </svg>
                  )}
                  {message.text}
                </p>
              )}
            </form>
          </div>

          {/* Section "Qu'est-ce qu'un journal de trading ?" */}
          <div className="text-left order-2 lg:order-1">
            <h3 className="font-bold mb-4" style={{ color: headingColor }}>{t('landing.footer.what_title')}</h3>
            <p style={{ color: bodyColor }}>
              {t('landing.footer.what_text')}
            </p>
          </div>

          {/* Section "Aide" */}
          <div className="text-left order-3 lg:order-2">
            <h3 className="font-bold mb-4" style={{ color: headingColor }}>{t('landing.footer.help')}</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/contact"
                  className={`${hoverColor} transition-colors block`}
                  style={{ color: bodyColor }}
                >
                  {t('landing.footer.contact')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/plateformes" 
                  className={`${hoverColor} transition-colors block`}
                  style={{ color: bodyColor }}
                >
                  {t('landing.footer.platforms')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/blog" 
                  className={`${hoverColor} transition-colors block`}
                  style={{ color: bodyColor }}
                >
                  {t('landing.footer.blog')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/help" 
                  className={`${hoverColor} transition-colors block`}
                  style={{ color: bodyColor }}
                >
                  {t('landing.footer.faq')}
                </Link>
              </li>
              <li>
                <Link
                  to="/affiliation"
                  className={`${hoverColor} transition-colors block`}
                  style={{ color: bodyColor }}
                >
                  {t('landing.footer.referral')}
                </Link>
              </li>
              <li>
                <Link
                  to="/our-partners"
                  className={`${hoverColor} transition-colors block`}
                  style={{ color: bodyColor }}
                >
                  {t('landing.footer.our_partners')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Section "Légal" */}
          <div className="text-left order-4 lg:order-3">
            <h3 className="font-bold mb-4" style={{ color: headingColor }}>{t('landing.footer.legal')}</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/terms"
                  className={`${hoverColor} transition-colors block`}
                  style={{ color: bodyColor }}
                >
                  {t('landing.footer.terms')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy"
                  className={`${hoverColor} transition-colors block`}
                  style={{ color: bodyColor }}
                >
                  {t('landing.footer.privacy')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/security"
                  className={`${hoverColor} transition-colors block`}
                  style={{ color: bodyColor }}
                >
                  {t('landing.footer.security')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/affiliate-terms"
                  className={`${hoverColor} transition-colors block`}
                  style={{ color: bodyColor }}
                >
                  {t('landing.footer.affiliate_terms')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Section copyright et mentions légales */}
        <div className="mt-12 pt-8 text-center" style={{ borderTop: footerBorder }}>
          <p className="text-[13px]" style={{ color: bodyColor }}>
            {t('landing.footer.facebook_notice')}
          </p>
          <p className="text-[13px] mt-4" style={{ color: bodyColor }}>
            {t('landing.footer.risk_notice')}
          </p>
          <p className="text-[13px] mt-4" style={{ color: bodyColor }}>
            {t('landing.footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

